<template>
  <GameFormatOptionVertical
    id="gmf_vertical"
    ref="gmf_vertical"
    :class="customSolutionLoading ? 'gw_loading no-events' : ''"
    tour="gmfselector"
    :data-tst="tstId"
    class="cssm"
    :minimized="minimized"
    :open="open"
    :hasBubble="(isICM || isEvents) && hasBubble"
    :hasTree="solElite.want_use && !solElite.must_create"
    :gameformat="gameformat"
    :customSolution="customSolution"
    @click="handleClick"
    @click:editsolution="openCustomSolutionsDialog"
    @click:bubble="toggleBubble"
    @click:tree="handleClickTree"
  >
    <template
      v-if="solElite.want_use && !solElite.must_create"
      #after
    >
      <GwMenu
        ref="gwmenu"
        :useTargetElement="true"
        placement="bottom-start"
        :isMovable="true"
        :isInside="false"
        style="position: absolute"
        @click.stop
      >
        <template #default="{ methods }">
          <div
            class="gw_menu"
            style="--clr-back: var(--clr-black-1-1); min-width: clamp(1px, 45rem, 100vw)"
          >
            <div class="text-normal-large weight_700 mb-2 cursor-grab z-index_4">
              {{ $t('solfigr.titles.summary') }}
              <div
                class="mdi mdi-close icon_btn pa-1"
                style="position: absolute; top: 0.5rem; right: 0.5rem"
                @click="methods.toggleOpen(false)"
              ></div>
            </div>
            <SolfigrSummary
              style="--solfigr-gap: 0.75rem"
              class="mt-2"
              :namespace
              :formResult="customSolution.apiData"
            >
            </SolfigrSummary>
          </div>
        </template>
      </GwMenu>
    </template>
  </GameFormatOptionVertical>
</template>

<script>
import GwMenu from '@/components/lib/GwMenu'
import GameFormatOptionVertical from '@/components/GameFormat/GameFormatOptionVertical'
import SolfigrSummary from '@/components/ConfigureSolution/SolfigrSummary'
import format_mix from '@/mixins/format_mix'
import { getBlinds } from '@/store/solfigr/solfigr_helpers'
import { sortByPosition } from '@/helpers/helpers'

export default {
  name: 'GameformatSelector',
  components: {
    SolfigrSummary,
    GameFormatOptionVertical,
    GwMenu,
  },
  mixins: [format_mix],
  props: {
    namespace: { String, required: true },
    minimized: Boolean,
    solElite: Object,
    hasBubble: { type: Boolean, default: true },
  },
  data() {
    return {
      open: false,
      gmix_hasDepthList: false,
    }
  },
  methods: {
    handleClickTree(e) {
      if (this.$refs.gwmenu) {
        this.$refs.gwmenu.toggleOpen(!this.$refs.gwmenu.open, {
          target: document.getElementById('gmf_vertical'),
        })
      }
    },
    handleClick() {
      if (!this.mobile) {
        this.toggleGmfSelektor()
        return
      }
      this.openSolutionsDialog()
    },
    toggleGmfSelektor() {
      this.$store.commit('globals_set', {
        key: 'floating_gmf_visible',
        value: !this.$store.getters.floating_gmf_visible,
      })
    },
    toggleBubble() {
      if (this.mobile) {
        const dialog = `bubble-factors-dialog${this.namespace ? `_namespace-${this.namespace}` : ''}`
        this.$store.dispatch('router_set_dialogs', dialog)
        return
      }
      this.$store.commit('globals_set', {
        key: 'floating_bubble_visible',
        value: !this.$store.getters.floating_bubble_visible,
      })
    },
    openCustomSolutionsDialog() {
      if (this.customSolutionLoading) return
      const tmpNamespace = 'tmp/primary'
      const dialog = `solfigr-create-custom-solution-dialog_namespace-${this.namespace || ''}_tmpNamespace-${tmpNamespace}_mode-keepboard`
      this.$store.dispatch('router_set_dialogs', dialog)
    },
    openSolutionsDialog() {
      const tmpNamespace = 'tmp/primary'
      this.$store.dispatch('poker_copy_module', {
        from_namespace: this.namespace,
        to_namespace: tmpNamespace,
        queryGroup: 'gmf-selektor',
      })
      this.$store.dispatch('poker_copy_module', {
        from_namespace: this.namespace,
        to_namespace: tmpNamespace,
        queryGroup: 'router-to-gmf-selektor',
      })
      this.$store.dispatch('poker_copy_module', {
        from_namespace: this.namespace,
        to_namespace: tmpNamespace,
        queryGroup: 'router',
      })

      const dialog = `solutions-dialog_tmpNamespace-${tmpNamespace}`
      this.$store.dispatch('router_set_dialogs', dialog)
    },
  },
  computed: {
    customSolutionLoading() {
      return (
        !this.solElite.cSol &&
        this.solElite?.want_use &&
        (this.solElite?.loading_what === 'custom_solution_tree' ||
          this.solElite?.loading_what === 'custom_solution')
      )
    },
    customSolution() {
      if (!this.solElite?.want_use || this.solElite?.must_create || this.customSolutionLoading) {
        return null
      }

      const cTree = this.solElite.cTree || null
      const players = cTree?.players || []
      const playerOOP = players.find(p => p.position === 'OOP')
      const playerIP = players.find(p => p.position === 'IP')
      let title = ''
      if (this.$store?.getters?.user_has_flag?.('SOL_3WAY')) {
        title = this.solElite.want_use
          ? players
              .sort((a, b) => sortByPosition(a.position, b.position))
              .map(p => this.getPlayerHtmlPosition(p.display_position))
              .join(this.$t('solfigr.vs'))
          : this.$t(`common.custom`)
      } else {
        title = this.solElite.want_use
          ? this.$t(`solfigr.oop_vs_ip`, {
              oop: this.getPlayerHtmlPosition(playerOOP?.display_position) || 'OOP',
              ip: this.getPlayerHtmlPosition(playerIP?.display_position) || 'IP',
            })
          : this.$t(`common.custom`)
      }

      // TODO use getter fn
      const allow_limp =
        cTree?.bet_sizes?.street_bet_sizes?.[0]?.position_bet_sizes?.[0]?.allow_limp

      const rakeCap = cTree?.rake?.cap_in_chips || 0
      const rakePercent = cTree?.rake?.pct_of_pot || 0

      const rake =
        cTree?.rake?.pct_of_pot || cTree?.rake?.cap_in_chips
          ? this.$t(`solfigr.gmf.rake`, { rakeCap, rakePercent })
          : ''

      const details =
        cTree?.starting_street === 'FLOP'
          ? {
              depth: this.$t('solfigr.gmf.stack', {
                num: this.numFixedUnit(Number(cTree?.players[0]?.stack), 'bb'),
              }),
              pot: this.$t('solfigr.gmf.pot', { num: this.numFixedUnit(Number(cTree?.pot), 'bb') }),
              ...(rake ? { rake } : {}),
            }
          : {
              stack_and_blinds: this.$t('solfigr.gmf.stack_and_blinds', {
                stack: this.numFixedUnit(
                  Math.min(
                    Number(cTree?.players[0]?.stack), //
                    Number(cTree?.players[1]?.stack),
                  ),
                  'bb',
                ),
                sb: this.numFixedUnit(Number(getBlinds(cTree?.players)?.SB), 'bb'),
                bb: this.numFixedUnit(Number(getBlinds(cTree?.players)?.BB), 'bb'),
              }),
              ...(Number(cTree?.ante) && {
                ante: this.$t('solfigr.gmf.ante', {
                  ante: this.numFixedUnit(Number(cTree?.ante), 'ante'),
                  ante_distribution_method: cTree?.ante_distribution_method
                    ? this.$t(`solfigr.ante_distribution_method.${cTree?.ante_distribution_method}`)
                    : '',
                }),
              }),
              limps: allow_limp ? this.$t('solfigr.has_limps') : this.$t('solfigr.no_limps'),
              ...(rake ? { rake } : {}),
            }

      const res = {
        title,
        ...(this.solElite.want_use && !this.solElite.must_create && { details }),
        apiData: cTree || {},
      }
      return res
    },

    gameformat() {
      return this.$store.getters[`${this.namespace ? `${this.namespace}/` : ''}gameformat`]
    },
    isICM() {
      return (
        (this.solElite?.is_using && this.solElite?.tournamentType === 'icm') ||
        (this.gameformat?.format === 'icm' && !this.solElite?.want_use)
      )
    },
    isEvents() {
      return this.gameformat?.format === 'icm_events'
    },
    mobile() {
      return this.$store.getters.globals_window_size.width < 1024
    },
    tstId() {
      if (this.customSolution) {
        const c = this.customSolution.apiData
        return `gmf_custom${
          c.players
            ? `_${c.players
                .map(p => `${p.position}_${parseInt(p.blind, 10) || 0}`)
                .sort()
                .join('_')}`
            : ''
        }${c.pot ? `_pot_${parseInt(c.pot, 10)}` : ''}${
          c.ante ? `_ante_${parseInt(c.ante, 10)}` : ''
        }`
      }
      const g = this.gameformat
      return `gmf_gwiz${g?.gametype ? `_${g.gametype}` : ''}${g?.depth ? `_depth_${g.depth}` : ''}${
        g?.stacks ? `_stack_${g.stacks}` : ''
      }`
    },
  },
}
</script>
